import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Container,
  Users,
  ShoppingCart,
  FileText,
  Settings,
  Bell,
  LogOut,
  Truck,
  ExternalLink,
  Globe,
  Car,
  FileEdit,
  MessageSquare,
  BookOpen,
  TrendingUp,
  Calculator,
  Phone,
  Video,
  BarChart3,
  Boxes,
  Route,
  UserCog,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { LanguageSwitcher } from '../LanguageSwitcher';

// Navigation organized by category
const navCategories = [
  {
    title: 'Yfirlit',
    titleKey: 'overview',
    items: [
      { key: 'dashboard', href: '/', icon: LayoutDashboard, label: 'Mælaborð' },
      { key: 'analytics', href: '/greining', icon: BarChart3, label: 'Tölfræði' },
    ],
  },
  {
    title: 'Rekstur',
    titleKey: 'operations',
    items: [
      { key: 'containers', href: '/gamar', icon: Container, label: 'Gámar' },
      { key: 'fleet', href: '/bilafloti', icon: Truck, label: 'Bílafloti' },
      { key: 'orders', href: '/pantanir', icon: ShoppingCart, label: 'Pantanir' },
      { key: 'invoices', href: '/reikningar', icon: FileText, label: 'Reikningar' },
      { key: 'alerts', href: '/tilkynningar', icon: Bell, label: 'Tilkynningar' },
    ],
  },
  {
    title: 'Viðskiptavinir',
    titleKey: 'customers',
    items: [
      { key: 'customers', href: '/vidskiptavinir', icon: Users, label: 'Viðskiptavinir' },
      { key: 'feedback', href: '/abendingar', icon: MessageSquare, label: 'Ábendingar' },
    ],
  },
  {
    title: 'Skýrslur',
    titleKey: 'reports',
    isNew: true,
    items: [
      { key: 'strategic_analysis', href: '/stefnugreining', icon: TrendingUp, label: 'Stefnugreining' },
      { key: 'roi_calculator', href: '/reikni', icon: Calculator, label: 'ROI Reiknivél' },
    ],
  },
  {
    title: 'Stjórnun',
    titleKey: 'admin',
    items: [
      { key: 'user_management', href: '/notendur', icon: UserCog, label: 'Notendur' },
      { key: 'fleet_assignment', href: '/bilafloti-skipulag', icon: Route, label: 'Bílaskipulag' },
      { key: 'container_assignment', href: '/gamar-skipulag', icon: Boxes, label: 'Gámaskipulag' },
      { key: 'cms', href: '/cms', icon: FileEdit, label: 'Efnisstjórnun' },
      { key: 'hero_videos', href: '/heimasida-myndbond', icon: Video, label: 'Myndbandsstjórnun' },
      { key: 'calls', href: '/simtol', icon: Phone, label: 'Símtöl' },
    ],
  },
  {
    title: 'Skjölun',
    titleKey: 'documentation',
    items: [
      { key: 'documentation', href: '/skjolun', icon: BookOpen, label: 'Tækniskjölun' },
    ],
  },
];

const externalLinks = [
  { key: 'website', href: 'https://litla.gamaleigan.is', label: 'Vefsíða', icon: Globe },
  { key: 'customer_portal', href: 'https://litla.gamaleigan.is/portal', label: 'Viðskiptavinagátt', icon: Users },
  { key: 'driver_app', href: 'https://okumenn.gamaleigan.is', label: 'Bílstjóraapp', icon: Car },
];

interface SidebarProps {
  onClose?: () => void;
}

export function Sidebar({ onClose }: SidebarProps) {
  const { t } = useTranslation('navigation');

  const handleClick = () => {
    if (onClose && window.innerWidth < 768) {
      onClose();
    }
  };

  return (
    <div className="flex h-screen w-64 flex-col bg-litla-dark">
      {/* Logo */}
      <div className="flex h-16 items-center justify-center border-b border-white/10">
        <div className="flex flex-col items-center">
          <span className="text-lg md:text-xl font-bold text-litla-primary">Litli Sorpstjórinn</span>
          <span className="text-[10px] md:text-xs text-gray-400">Stjórnborð</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-6">
        {navCategories.map((category) => (
          <div key={category.titleKey}>
            <div className="flex items-center justify-between px-3 mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t(`categories.${category.titleKey}`, category.title)}
              </p>
              {category.isNew && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-500 text-white">
                  NÝTT
                </span>
              )}
            </div>
            <div className="space-y-1">
              {category.items.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.href}
                  onClick={handleClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-litla-primary text-white'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    )
                  }
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">{t(`menu.${item.key}`, item.label)}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        {/* Settings (separate) */}
        <div>
          <div className="px-3 mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {t('categories.settings', 'Stillingar')}
            </p>
          </div>
          <NavLink
            to="/stillingar"
            onClick={handleClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-litla-primary text-white'
                  : 'text-gray-300 hover:bg-white/10 hover:text-white'
              )
            }
          >
            <Settings className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">{t('menu.settings', 'Stillingar')}</span>
          </NavLink>
        </div>

        {/* External Links */}
        <div>
          <div className="px-3 mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {t('categories.links', 'Tenglar')}
            </p>
          </div>
          <div className="space-y-1">
            {externalLinks.map((item) => (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
              >
                <item.icon className="h-5 w-5" />
                <span className="truncate">{t(`external.${item.key}`, item.label)}</span>
                <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Language Switcher & User section */}
      <div className="border-t border-white/10 p-4 space-y-3">
        <div className="flex justify-center">
          <LanguageSwitcher />
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-white/10">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-litla-primary text-white">
            G
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-white">Gunnar Walsh</p>
            <p className="text-xs text-gray-400">{t('user_role', 'Stjórnandi')}</p>
          </div>
          <button
            className="rounded-lg p-2 text-gray-400 hover:bg-white/10 hover:text-white"
            title={t('menu.logout', 'Útskrá')}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
